import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  PriceDetails,
  PriceReference,
  RgPriceBreakdownSection,
} from '@shared/models';

/**
 * Master-detail content for a single InventoryItemPriceHistory entry. Rendered
 * inside the price-history table's expandable detail row.
 *
 * References that point at a local item (uid set) open that inventory item in a
 * new tab via a plain `[href]` anchor; external price sources (uid null) are
 * shown as non-clickable text.
 *
 * IMPORTANT: `references` is computed once whenever the `details` input changes
 * and bound with `trackBy`. Do NOT bind the template `*ngFor` to a method call
 * (e.g. `getReferences(details)`): that returns a new array with new object
 * identities on every change-detection run, so `*ngFor` tears down and rebuilds
 * the anchor DOM continuously — a CD cycle between mousedown and click then
 * swallows the click and the link "does nothing".
 *
 * R19: knowingly unrendered by the test suite. It has no spec of its own, and
 * its only render site is `inventory-price-history`'s master-detail template —
 * which is itself unrendered, since both of that component's hosts gate it on
 * `canSeePriceCalculation()` and no spec seeds the token that opens it. A
 * pre-existing gap: this component arrived with the 2026-08-05 merge and was
 * already unrendered before the logic-only conversion.
 */
@Component({
  selector: 'app-inventory-price-detail',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './inventory-price-detail.component.html',
  styleUrl: './inventory-price-detail.component.scss',
})
export class InventoryPriceDetailComponent {
  private _details: PriceDetails | null = null;

  /** Stable, index-aligned references, recomputed only when `details` changes. */
  public references: PriceReference[] = [];

  /**
   * Source links (e.g. from the GPT source), recomputed when `details` changes.
   * `verified` marks links backed by an API citation or an actual web_search
   * source (vs. links the model merely stated).
   */
  public links: { url: string; verified: boolean }[] = [];

  /**
   * Display labels for the dynamic API enum values (`method`, `aggregation`,
   * `match_criteria`, matched-on pairs). Computed once when `details` changes —
   * consistent with `references` and the component's compute-on-input philosophy,
   * so the template binds plain strings instead of method calls.
   */
  public methodLabel = '–';
  public aggregationLabel = '–';
  public matchCriteriaLabel = '–';
  public matchedOnLabel = '–';
  public candidateCountLabel = '–';

  /** German formula breakdown (rg-calculated only), recomputed when details change. */
  public breakdown: RgPriceBreakdownSection[] = [];

  @Input()
  set details(value: PriceDetails | null) {
    this._details = value;
    this.references = this.buildReferences(value);
    this.links = this.buildLinks(value);
    this.methodLabel = this.translateValue('methodValues', value?.method) || '–';
    this.aggregationLabel =
      this.translateValue('aggregationValues', value?.aggregation) || '–';
    this.matchCriteriaLabel = this.formatMatchCriteria(value) || '–';

    // The rg-calculated (formula) method is parameter-driven, not a candidate
    // match, so "Gefunden über" and "Anzahl Kandidaten" have no meaning here.
    const isFormula = value?.origin === 'rg-calculated';
    this.matchedOnLabel = isFormula ? '–' : this.formatMatchedOn(value) || '–';
    this.candidateCountLabel =
      isFormula || value?.candidate_count == null
        ? '–'
        : String(value.candidate_count);

    const breakdown = value?.extra?.['breakdown'];
    this.breakdown = Array.isArray(breakdown)
      ? (breakdown as RgPriceBreakdownSection[])
      : [];
  }

  get details(): PriceDetails | null {
    return this._details;
  }

  /** keyvalue comparator that keeps the original (insertion) order. */
  public keepOrder = (): number => 0;

  constructor(
    private router: Router,
    private translate: TranslateService,
  ) {}

  /** Stable identity for *ngFor so anchor DOM is not rebuilt each CD cycle. */
  public trackByReference(_index: number, ref: PriceReference): string | number {
    return ref.item_uid ?? ref.item_id ?? _index;
  }

  /** Serialized URL to view a referenced inventory item by its uid. */
  public referenceUrl(uid: string): string {
    return this.router.serializeUrl(this.router.createUrlTree(['/view', uid]));
  }

  /**
   * Renders the matched values as "criterion: value, ...". The matched values
   * are stored as top-level keys named after each entry in `match_criteria`.
   * The criterion *names* are translated; the matched values are raw data.
   */
  public formatMatchedOn(details: PriceDetails | null): string {
    if (!details?.match_criteria) {
      return '';
    }
    return details.match_criteria
      .map(
        (criterion) =>
          `${this.translateCriterion(criterion)}: ${details[criterion] ?? '–'}`,
      )
      .join(', ');
  }

  /** Joins the translated `match_criteria` names with ", ". */
  private formatMatchCriteria(details: PriceDetails | null): string {
    if (!details?.match_criteria) {
      return '';
    }
    return details.match_criteria
      .map((criterion) => this.translateCriterion(criterion))
      .join(', ');
  }

  /** Translates a single `match_criteria` name via `criteriaValues`. */
  private translateCriterion(criterion: string): string {
    return this.translateValue('criteriaValues', criterion);
  }

  /**
   * Translates a dynamic API enum value via
   * `inventory.priceCalculation.<namespace>.<value>`. Falls back to the raw
   * value when no translation exists, so unmapped API values stay readable.
   */
  private translateValue(
    namespace: string,
    value: string | null | undefined,
  ): string {
    if (!value) {
      return '';
    }
    const key = `inventory.priceCalculation.${namespace}.${value}`;
    const translated = this.translate.instant(key);
    return translated === key ? value : translated;
  }

  /**
   * Builds the displayable source links (e.g. from the GPT source). Prefers the
   * flagged `extra.source_links`; falls back to the plain `extra.links` (treated
   * as unverified) for older entries written before flags existed.
   */
  private buildLinks(
    details: PriceDetails | null,
  ): { url: string; verified: boolean }[] {
    const sourceLinks = details?.extra?.source_links;
    if (Array.isArray(sourceLinks)) {
      return sourceLinks
        .filter((link) => link && typeof link.url === 'string' && link.url.trim().length > 0)
        .map((link) => ({ url: link.url, verified: !!link.verified }));
    }

    const links = details?.extra?.links;
    if (Array.isArray(links)) {
      return links
        .filter((link): link is string => typeof link === 'string' && link.trim().length > 0)
        .map((url) => ({ url, verified: false }));
    }

    return [];
  }

  /**
   * Zips the index-aligned reference arrays into objects for display.
   */
  private buildReferences(details: PriceDetails | null): PriceReference[] {
    if (!details) {
      return [];
    }
    const ids = details.reference_item_ids ?? [];
    const uids = details.reference_item_uids ?? [];
    const values = details.reference_values ?? [];
    const length = Math.max(ids.length, uids.length, values.length);
    const references: PriceReference[] = [];
    for (let i = 0; i < length; i++) {
      references.push({
        item_id: ids[i] ?? null,
        item_uid: uids[i] ?? null,
        value: values[i] ?? null,
      });
    }
    return references;
  }
}
